import { useEffect, useState } from "react";
import api from "../../services/api";

const EMPTY_FORM = {
  code: "",
  name: "",
  sellRate: "",
  costRate: "",
  vatRate: "21",
  billable: true,
  active: true,
};

export default function UursoortenPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/tenant-hour-types");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Uursoorten laden mislukt");
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!String(form.name || "").trim()) return;

    try {
      setSaving(true);
      setError("");
      const payload = {
        code: String(form.code || "").trim().toUpperCase(),
        name: String(form.name || "").trim(),
        sellRate: Number(form.sellRate || 0),
        costRate: Number(form.costRate || 0),
        vatRate: Number(form.vatRate || 21),
        billable: form.billable !== false,
        active: form.active !== false,
      };

      if (editingId) {
        await api.patch(`/tenant-hour-types/${editingId}`, payload);
      } else {
        await api.post("/tenant-hour-types", payload);
      }

      setForm(EMPTY_FORM);
      setEditingId("");
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.message || "Uursoort opslaan mislukt");
    } finally {
      setSaving(false);
    }
  }

  function startEdit(item) {
    setEditingId(String(item._id));
    setForm({
      code: item.code || "",
      name: item.name || "",
      sellRate: toNumberInputValue(item.sellRate),
      costRate: toNumberInputValue(item.costRate),
      vatRate: toNumberInputValue(item.vatRate || 21),
      billable: item.billable !== false,
      active: item.active !== false,
    });
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Uursoorten</h2>
        <p className="text-sm text-gray-600">
          Beheer interne kostprijs en verkoopprijs per uursoort.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="rounded-xl bg-white p-4 shadow space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-semibold">{editingId ? "Uursoort bewerken" : "Nieuwe uursoort"}</h3>
          {editingId && (
            <button
              type="button"
              onClick={() => {
                setEditingId("");
                setForm(EMPTY_FORM);
              }}
              className="rounded border px-3 py-1.5 text-sm hover:bg-gray-50"
            >
              Annuleren
            </button>
          )}
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <input
            value={form.code}
            onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
            placeholder="Code"
            className="border rounded p-2"
          />
          <input
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="Naam *"
            className="border rounded p-2"
            required
          />
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.sellRate}
            onChange={(e) => setForm((prev) => ({ ...prev, sellRate: e.target.value }))}
            placeholder="Verkoopprijs per uur"
            className="border rounded p-2"
          />
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.costRate}
            onChange={(e) => setForm((prev) => ({ ...prev, costRate: e.target.value }))}
            placeholder="Interne kostprijs per uur"
            className="border rounded p-2"
          />
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.vatRate}
            onChange={(e) => setForm((prev) => ({ ...prev, vatRate: e.target.value }))}
            placeholder="BTW %"
            className="border rounded p-2"
          />
          <div className="flex items-center gap-4 rounded border p-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.billable}
                onChange={(e) => setForm((prev) => ({ ...prev, billable: e.target.checked }))}
              />
              Facturabel
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
              />
              Actief
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? "Opslaan..." : editingId ? "Uursoort bijwerken" : "Uursoort opslaan"}
        </button>
      </form>

      <section className="rounded-xl bg-white p-4 shadow">
        <h3 className="mb-3 font-semibold">Overzicht</h3>
        {loading && <p className="text-gray-500">Laden...</p>}
        {error && <p className="text-red-600">{error}</p>}
        {!loading && !error && items.length === 0 && (
          <p className="text-gray-500">Nog geen uursoorten.</p>
        )}

        {!loading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-3">Code</th>
                  <th className="py-2 pr-3">Naam</th>
                  <th className="py-2 pr-3">Verkoop</th>
                  <th className="py-2 pr-3">Kostprijs</th>
                  <th className="py-2 pr-3">BTW</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 text-right">Actie</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item._id} className="border-b">
                    <td className="py-2 pr-3">{item.code || "-"}</td>
                    <td className="py-2 pr-3">{item.name || "-"}</td>
                    <td className="py-2 pr-3">€ {toMoney(item.sellRate)}</td>
                    <td className="py-2 pr-3">€ {toMoney(item.costRate)}</td>
                    <td className="py-2 pr-3">{Number(item.vatRate || 0)}%</td>
                    <td className="py-2 pr-3">
                      {item.active === false ? "Inactief" : item.billable === false ? "Niet facturabel" : "Actief"}
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        onClick={() => startEdit(item)}
                        className="rounded bg-green-600 px-3 py-1.5 text-xs text-white hover:bg-green-700"
                      >
                        Bewerken
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function toMoney(value) {
  return Number(value || 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function toNumberInputValue(value) {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? String(numeric) : "";
}
